const ABORT_ERROR_NAME = 'AbortError';
const DEFAULT_ERROR_MESSAGE = 'An error has occurred. Please contact Support.';
const HTTP_NO_CONTENT = 204;

class Api {
  constructor() {
    this._inflightRequests = {};
    this._token = null;
  }

  _getPathnameFromUrl(url) {
    return url.split('?')[0];
  }

  _makeAbortSignal(url) {
    const pathname = this._getPathnameFromUrl(url);
    const abortController = new AbortController();
    this._inflightRequests[pathname] = abortController;
    return abortController.signal;
  }

  _removeInflightRequest(url) {
    const pathname = this._getPathnameFromUrl(url);
    delete this._inflightRequests[pathname];
  }

  _abortDuplicateRequest(url) {
    const pathname = this._getPathnameFromUrl(url);
    const duplicateRequestAbortController = this._inflightRequests[pathname];
    if (duplicateRequestAbortController) {
      duplicateRequestAbortController.abort();
      this._removeInflightRequest(url);
    }
  }

  _isJsonResponse(response) {
    const hasContent = response.status !== HTTP_NO_CONTENT;
    const isJsonTypeResponse = response.headers
      .get('Content-Type')
      ?.includes('application/json');

    return hasContent && isJsonTypeResponse;
  }

  _getRequestHeaders() {
    const requestHeaders = {
      'Content-Type': 'application/json',
      Authorization: `Token token=${this._token}`,
    };

    return requestHeaders;
  }

  _makeRequest(url, method, payload) {
    this._abortDuplicateRequest(url);
    const config = {
      method: method,
      signal: this._makeAbortSignal(url),
      headers: this._getRequestHeaders(),
    };

    if (payload) {
      config.body = JSON.stringify(payload);
    }

    return fetch(url, config)
      .then(async (response) => {
        let data = null;
        if (this._isJsonResponse(response)) {
          data = await response.json();
        }
        if (response.ok) {
          return data;
        } else {
          return Promise.reject(data);
        }
      })
      .finally(() => this._removeInflightRequest(url));
  }

  setToken(newToken) {
    this._token = newToken;
  }

  get(url) {
    return this._makeRequest(url, 'GET');
  }

  post(url, payload) {
    return this._makeRequest(url, 'POST', payload);
  }

  put(url, payload) {
    return this._makeRequest(url, 'PUT', payload);
  }

  patch(url, payload) {
    return this._makeRequest(url, 'PATCH', payload);
  }

  delete(url, payload) {
    return this._makeRequest(url, 'DELETE', payload);
  }
}

// Code calling any of the methods in this Api class should process caught errors
// through this function.
const getErrorMessage = (error) => {
  // This module handles cancelling duplicate inflight requests, so we suppress
  // this type of error.
  if (error?.name === ABORT_ERROR_NAME) {
    return null;
  } else if (error?.error) {
    return error.error;
  }
  // Some API endpoints return an `errors` key, which is currently not
  // handled here, as no consumers of this modules have had that need. When and
  // if this functionality is needed, the legacy fetch module can provide a
  // blueprint for handling this:
  // https://github.com/ThriveTRM/true-app/blob/development/app/assets/javascripts/modules/api/fetch.js#L15-L26
  // Note: Unless there's a very compelling resason to do so, we do not want to
  // support an `errors` key that can have either an array or object value.
  // The use of Object is an outlier. If this is ever an issue, we should update
  // the endpoint to return the errors as an array rather than supporting here.
  return DEFAULT_ERROR_MESSAGE;
};

export { getErrorMessage };
export default new Api();
